import { DataGridPremiumProps, GridCellModes, GridCellModesModel, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";

/**
 * Extended column definition type that includes the isSingleClickEditable property.
 * Use this type when defining columns that should support single-click editing.
 * 
 * @example
 * ```tsx
 * const columns: SingleClickEditableColDef[] = [
 *   {
 *     field: "systemId",
 *     editable: true,
 *     isSingleClickEditable: true,
 *     type: "singleSelect",
 *     // ... other props
 *   }
 * ];
 * ```
 */
export type SingleClickEditableColDef = GridColDef & {
  /**
   * If `true`, clicking the cell will immediately enter edit mode (single-click editing).
   * Requires the column to also have `editable: true`.
   * @default false
   */
  isSingleClickEditable?: boolean;
};

export interface UseSingleClickEditingProps {
  /**
   * Controlled cell modes model. If provided, the hook will use this instead of internal state.
   */
  cellModesModel?: GridCellModesModel;
  /**
   * Callback fired when the cell modes model changes.
   * If provided, the hook will call this instead of updating internal state.
   */
  onCellModesModelChange?: DataGridPremiumProps["onCellModesModelChange"];
  /**
   * Optional existing onCellClick handler to chain with.
   */
  onCellClick?: DataGridPremiumProps["onCellClick"];
  /**
   * Optional existing onCellKeyDown handler to chain with.
   */
  onCellKeyDown?: DataGridPremiumProps["onCellKeyDown"];
}

export interface UseSingleClickEditingResult {
  cellModesModel: GridCellModesModel;
  onCellModesModelChange: DataGridPremiumProps["onCellModesModelChange"];
  onCellClick: DataGridPremiumProps["onCellClick"];
  onCellKeyDown: DataGridPremiumProps["onCellKeyDown"];
}

/**
 * Hook to enable single-click editing for DataGrid cells.
 *
 * Columns can opt-in to single-click editing by setting `isSingleClickEditable: true`.
 * This is separate from the `editable` property - a column must have both
 * `editable: true` and `isSingleClickEditable: true` for single-click editing to work.
 *
 * @example
 * ```tsx
 * const singleClickEditingProps = useSingleClickEditing({
 *   cellModesModel: props.cellModesModel,
 *   onCellModesModelChange: props.onCellModesModelChange,
 *   onCellClick: props.onCellClick,
 * });
 *
 * return <DataGrid {...singleClickEditingProps} />;
 * ```
 */
export const useSingleClickEditing = (
  props: UseSingleClickEditingProps = {}
): UseSingleClickEditingResult => {
  const [internalCellModesModel, setInternalCellModesModel] =
    useState<GridCellModesModel>({});

  const isControlled = props.cellModesModel !== undefined;
  const cellModesModel = isControlled
    ? props.cellModesModel
    : internalCellModesModel;

  const handleCellModesModelChange = useCallback<
    NonNullable<DataGridPremiumProps["onCellModesModelChange"]>
  >(
    (newModel, details) => {
      if (props.onCellModesModelChange) {
        props.onCellModesModelChange(newModel, details);
      } else {
        setInternalCellModesModel(newModel);
      }
    },
    [props.onCellModesModelChange]
  );

  const handleCellClick = useCallback<
    NonNullable<DataGridPremiumProps["onCellClick"]>
  >(
    (params: GridCellParams, event, details) => {
      // Call the original onCellClick handler if provided
      if (props.onCellClick) {
        props.onCellClick(params, event, details);
      }

      // Check if this column has single-click editing enabled
      const colDef = params.colDef as any;
      if (!colDef.isSingleClickEditable) {
        return;
      }

      // Ignore portal clicks (e.g., clicks inside dropdowns/popovers)
      if (
        (event.target as HTMLElement).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      const updateModel = (prevModel: GridCellModesModel): GridCellModesModel => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      };

      if (props.onCellModesModelChange) {
        // Controlled mode: compute new model and call the callback
        const newModel = updateModel(cellModesModel ?? {});
        props.onCellModesModelChange(newModel, details);
      } else {
        // Uncontrolled mode: update internal state
        setInternalCellModesModel(updateModel);
      }
    },
    [props.onCellClick, props.onCellModesModelChange, cellModesModel]
  );

  const handleCellKeyDown = useCallback<
    NonNullable<DataGridPremiumProps["onCellKeyDown"]>
  >(
    (params, event, details) => {
      // Call the original onCellKeyDown handler if provided
      if (props.onCellKeyDown) {
        props.onCellKeyDown(params, event, details);
      }

      // Check if this column has single-click editing enabled
      const colDef = params.colDef as any;
      if (!colDef.isSingleClickEditable) {
        return;
      }

      // Handle Delete key to clear value
      if (event.key === "Delete" || event.key === "Backspace") {
        // Only clear if the cell is not in edit mode
        const cellMode = cellModesModel?.[params.id]?.[params.field]?.mode;
        event.preventDefault();
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: "",
        });
      }
    },
    [props.onCellKeyDown, cellModesModel]
  );

  return {
    cellModesModel: cellModesModel ?? {},
    onCellModesModelChange: handleCellModesModelChange,
    onCellClick: handleCellClick,
    onCellKeyDown: handleCellKeyDown,
  };
};

export default useSingleClickEditing;
